import { I18n } from 'react-redux-i18n';
import moment from 'moment';
import {
  formatPrice,
  formatTextFloat,
  formatValueBillion,
} from '../../../../component/helpers/Text';
import { isQuarterlyData } from '../../../../component/technical/charting/trading-view/helper';
import chartingService from '../Charting';
import { REDUCER_NAME as COMMON_REDUCER_NAME } from '../../../../component/app/commonReducer';
import store from '../../../../component/app/store';
import { SYMBOL_TYPES } from './Const';
import { isExpired, isNotLogin } from '../../../../component/helpers/Common';

const mapResolution = {
  1: 'EachMinute',
  5: 'EachFiveMinutes',
  15: 'EachFifteenMinutes',
  30: 'EachThirteenMinutes',
  60: 'EachOneHour',
  D: 'Daily',
  W: 'Weekly',
  M: 'Monthly',
  '3M': 'Quarterly',
  '12M': 'Yearly',
  '1D': 'Daily',
  '1W': 'Weekly',
  '1M': 'Monthly',
};

const getLang = () => store.getState().i18n.locale;

const getUser = () => store.getState().userInfo;

const getIssueMethodKey = () => {
  const mapLang = {
    en: 'en_IssueMethodName',
    vi: 'issueMethodName',
  };
  return mapLang[getLang()];
};

const getInternalTransactionName = () => {
  const mapLang = {
    en: 'en_FullName',
    vi: 'fullName',
  };

  return mapLang[getLang()];
};

const formatTime = time => {
  if (!time) {
    return '--';
  }
  const date = moment(time).toDate();
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

const formatPercent = percent => {
  return percent % 1 === 0 ? percent : formatTextFloat(percent);
};

const timeScaleMarkTypes = {
  Earning: {
    color: 'brown',
    label: 'E',
    tooltips: [
      {
        i18nKey: 'revenue',
        getValue: item => formatTextFloat(formatValueBillion(item.revenue)),
      },
      {
        i18nKey: 'profit',
        getValue: item => formatTextFloat(formatValueBillion(item.profit)),
      },
      {
        i18nKey: 'periodEnding',
        getValue: item =>
          `Q${item.lengthReport || '--'} - ${item.yearReport || '--'}`,
      },
    ],
  },
  Dividend: {
    color: 'yellow',
    label: 'D',
    tooltips: [
      {
        i18nKey: 'dividends',
        getValue: item => formatTextFloat(item.valuePershare, 0),
      },
      {
        i18nKey: 'exRatio',
        getValue: item => formatPercent(item.exerciseRate * 100),
      },
      {
        i18nKey: 'exrightDate',
        getValue: item => {
          return formatTime(item.exrightDate);
        },
      },
    ],
  },
  ShareIssuance: {
    color: 'blue',
    label: 'I',
    tooltips: [
      {
        i18nKey: 'issueMethod',
        getValue: item => item[getIssueMethodKey()] || '--',
      },
      {
        i18nKey: 'volume',
        getValue: item => formatTextFloat(item.issueVolumn, 0),
      },
      {
        i18nKey: 'exRatio',
        getValue: item => formatPercent(item.exerciseRatio * 100),
      },
      {
        i18nKey: 'exIssueDate',
        getValue: item => formatTime(item.exrightDate),
      },
    ],
  },
  ShareIssuancePlan: {
    color: 'blue',
    label: 'I',
    tooltips: [
      {
        i18nKey: 'issueMethod',
        getValue: item => item[getIssueMethodKey()],
      },
      {
        i18nKey: 'planVolume',
        getValue: item => formatTextFloat(item.planVolumn, 0),
      },
      {
        i18nKey: 'exRatio',
        getValue: item => formatPercent(item.exerciseRatio * 100),
      },
    ],
  },
};

const isRegisterType = item => item.tradeStatusCode === 'R';
const getMarkValue = item => {
  return isRegisterType(item)
    ? Math.abs(item.shareRegister)
    : Math.abs(item.shareAcquire);
};

const markTypes = {
  InternalTransaction: {
    S: {
      color: 'red',
      label: 'S',
      i18nKey: 'sell',
      getValue: item => formatTextFloat(getMarkValue(item), 0),
    },
    B: {
      color: 'green',
      label: 'B',
      i18nKey: 'buy',
      getValue: item => formatTextFloat(getMarkValue(item), 0),
    },
  },
};

const getTimescaleTooltip = (eventItem, item) => {
  const user = getUser();
  if (isNotLogin(user)) {
    return [I18n.t('layout.guest.guestFullText')];
  }

  if (isExpired(user)) {
    return [I18n.t('layout.guest.expiredFullText')];
  }

  return eventItem.tooltips.map(tooltip =>
    I18n.t(`charting.${tooltip.i18nKey}`, { value: tooltip.getValue(item) }),
  );
};

const getUnixTime = date => {
  const dateTime = moment(date).toDate();
  const utcDate = new Date(
    Date.UTC(
      dateTime.getFullYear(),
      dateTime.getMonth(),
      dateTime.getDate(),
      dateTime.getHours(),
      dateTime.getMinutes(),
      dateTime.getSeconds(),
      dateTime.getMilliseconds(),
    ),
  );
  return Math.round(utcDate.getTime() / 1000);
};

const formatTimescaleMarks = (result, item) => {
  const timeString =
    item.type === 'Dividend' ? item.exrightDate : item.publicDate;
  if (!timeString) {
    return result;
  }
  const obj = {
    time: getUnixTime(timeString),
    id: item.id,
    labelFontColor: '#ffffff',
    minSize: 10,
  };
  if (!timeScaleMarkTypes[item.type]) {
    return result;
  }
  let type = item.type;
  if (type === 'ShareIssuance' && item.issueStatusCode === 'P') {
    type = 'ShareIssuancePlan';
  }
  return result.concat({
    ...obj,
    color: timeScaleMarkTypes[type].color,
    label: timeScaleMarkTypes[type].label,
    tooltip: [''].concat(getTimescaleTooltip(timeScaleMarkTypes[type], item)),
  });
};

const getMarkTooltip = (item, mark) => {
  const user = getUser();
  if (isNotLogin(user)) {
    return I18n.t('layout.guest.guestFullText');
  }

  if (isExpired(user)) {
    return I18n.t('layout.guest.expiredFullText');
  }

  if (item.tradeStatusCode === 'D' && item.shareAcquire === 0) {
    return `
      <div style="margin-bottom: 5px">\
        <div><strong>${item[getInternalTransactionName()]}</strong></div>
        <div style="margin-top: 5px">
          <strong>
            ${I18n.t(`charting.${mark.i18nKey}NotHappen`, {
              value: formatTextFloat(item.shareRegister, 0),
            })}
          </strong>
        </div>
        <div style="margin-top: 5px">
          <strong>
              ${I18n.t(`charting.${mark.i18nKey}NotHappenStatus`)}
          </strong>
        </div>
      </div>
    `;
  }

  const i18nKey = isRegisterType(item)
    ? `charting.${mark.i18nKey}Register`
    : `charting.${mark.i18nKey}`;

  return `
    <div style="margin-bottom: 5px">
      <strong>${item[getInternalTransactionName()] || '--'}</strong>
    </div>
    <div>
      <strong>
        ${I18n.t(i18nKey, {
          value: mark.getValue(item),
        })}
      </strong>
    </div>
  `;
};

const formatMarks = (result, item) => {
  const obj = {
    time: getUnixTime(item.publicDate),
    id: item.id,
    minSize: 20,
  };
  if (!markTypes[item.type] || !markTypes[item.type][item.actionTypeCode]) {
    return result;
  }
  const mark = markTypes[item.type][item.actionTypeCode];
  return result.concat({
    ...obj,
    labelFontColor: mark.color,
    color: {
      border: mark.color,
      background: 'transparent',
    },
    label: mark.label,
    text: getMarkTooltip(item, mark),
  });
};

const getEventParam = (symbolInfo, from, to) => ({
  OrganCode: symbolInfo.code,
  From: new Date(from * 1000).toISOString(),
  To: new Date(to * 1000).toISOString(),
});

export const getPrice = (symbolInfo, value) => {
  if (
    symbolInfo.type === SYMBOL_TYPES.STOCK ||
    symbolInfo.type === SYMBOL_TYPES.COVERED_WARRANT
  ) {
    return formatPrice(value);
  }

  return value;
};

const getBarTime = (time, resolution) => {
  if (resolution.includes('D')) {
    const date = moment(time).toDate();

    return Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds(),
    );
  }

  return moment(time)
    .toDate()
    .getTime();
};

const getBarParams = (symbolInfo, resolution, from, to) => {
  const mapTypeToParam = {
    [SYMBOL_TYPES.STOCK]: 'Stock',
    [SYMBOL_TYPES.INDICES]: 'Index',
    [SYMBOL_TYPES.FUTURES]: 'Derivative',
    [SYMBOL_TYPES.ECONOMY]: 'Economy',
    [SYMBOL_TYPES.COVERED_WARRANT]: 'CW',
  };
  return {
    Code: symbolInfo.code,
    Frequency: mapResolution[resolution],
    From: new Date(from * 1000).toISOString(),
    To: new Date(to * 1000).toISOString(),
    Type: mapTypeToParam[symbolInfo.type],
  };
};

const getIndicatorParams = (symbolInfo, resolution, from, to) => {
  const [_, indicatorCode, ticker] = symbolInfo.name.split(':');
  const organization = store.getState()[COMMON_REDUCER_NAME]
    .listOrganizationByTicker[ticker];
  return {
    OganCode: organization ? organization.organCode : ticker,
    Code: indicatorCode,
    Frequency: mapResolution[resolution],
    From: new Date(from * 1000).toISOString(),
    To: new Date(to * 1000).toISOString(),
  };
};

export const isIndicator = symbolInfo => {
  return symbolInfo.name.includes('INDICATOR');
};

const formatBar = (resolution, isFormatPrice = false) => item => ({
  time: getBarTime(item.tradingDate, resolution),
  low: isFormatPrice ? formatPrice(item.lowestPrice) : item.lowestPrice,
  high: isFormatPrice ? formatPrice(item.highestPrice) : item.highestPrice,
  open: isFormatPrice ? formatPrice(item.openPrice) : item.openPrice,
  close: isFormatPrice ? formatPrice(item.closePrice) : item.closePrice,
  volume: item.totalMatchVolume,
});

const isValidIndicatorInterval = (symbolInfo, resolution) => {
  const [_, indicatorName] = symbolInfo.name.split(':');
  if (isQuarterlyData(indicatorName)) {
    return resolution === 'M';
  }

  return ['D', '1D', 'W', '1W', '1M', 'M'].includes(resolution);
};

export default {
  getBars: async (symbolInfo, resolution, from, to) => {
    if (isIndicator(symbolInfo)) {
      if (!isValidIndicatorInterval(symbolInfo, resolution)) {
        return null;
      }
      const data = await chartingService.getIndicator(
        getIndicatorParams(symbolInfo, resolution, from, to),
      );
      if (!data || data.items === null) {
        return null;
      }

      return data.items.map(formatBar(resolution));
    }

    const data = await chartingService.getTradingViewData(
      getBarParams(symbolInfo, resolution, from, to),
    );
    if (!data || data.items === null) {
      return null;
    }

    return data.items.map(
      formatBar(
        resolution,
        symbolInfo.type === SYMBOL_TYPES.STOCK ||
          symbolInfo.type === SYMBOL_TYPES.COVERED_WARRANT,
      ),
    );
  },

  getTimescaleMarks: async (symbolInfo, from, to) => {
    const response = await chartingService.getStockEvents(
      getEventParam(symbolInfo, from, to),
    );
    if (!response.data || !Array.isArray(response.data.items)) {
      return [];
    }

    return response.data.items.reduce(formatTimescaleMarks, []);
  },

  getMarks: async (symbolInfo, from, to) => {
    const response = await chartingService.getStockEvents(
      getEventParam(symbolInfo, from, to),
    );
    if (!response.data || !Array.isArray(response.data.items)) {
      return [];
    }

    return response.data.items.reduce(formatMarks, []);
  },
};
